import { fetchData, setHeaderOptions } from "#/api/apiHelpers";

export type MessageTemplateStatus = "draft" | "active" | "archived" | string;

export type MessageTemplateListItem = {
  id: string;
  name?: string | null;
  status?: MessageTemplateStatus | null;
  key?: string | null;
  title?: string | null;
  is_active?: boolean | null;
  updated_at?: string | null;
  created_at?: string | null;
};

export type MessageTemplateDetail = MessageTemplateListItem & {
  content?: string | null;
  key?: string | null;
  title?: string | null;
  description?: string | null;
  is_active?: boolean | null;
  user_variable_keys?: string[] | null;
  body?: string | null;
};

export type ListMessageTemplatesParams = {
  limit?: number;
  offset?: number;
  q?: string;
};

export type CreateMessageTemplateRequest = {
  name: string;
  body: string;
  title?: string;
  description?: string;
  is_active?: boolean;
  user_variable_keys?: string[];
};

export type UpdateMessageTemplateRequest = {
  name?: string;
  body?: string;
  title?: string;
  description?: string;
  is_active?: boolean;
  user_variable_keys?: string[];
};

const BASE_ROUTE = "/admin/message_template";

function toMessageTemplateKey(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export async function listMessageTemplates(
  params: ListMessageTemplatesParams = {},
  options?: { signal?: AbortSignal }
): Promise<MessageTemplateListItem[]> {
  const searchParams = new URLSearchParams();

  if (typeof params.limit === "number") {
    searchParams.set("limit", String(params.limit));
  }

  if (typeof params.offset === "number") {
    searchParams.set("offset", String(params.offset));
  }

  if (params.q) {
    searchParams.set("q", params.q);
  }

  searchParams.set("sort", "updated_at");
  searchParams.set("order", "desc");

  const query = searchParams.toString();
  const request = setHeaderOptions({ method: "GET" });

  return fetchData<MessageTemplateListItem[]>(
    `${BASE_ROUTE}${query ? `?${query}` : ""}`,
    {
      ...request,
      signal: options?.signal,
    }
  );
}

export async function getMessageTemplateById(
  id: string,
  options?: { signal?: AbortSignal }
): Promise<MessageTemplateDetail> {
  if (!id) {
    throw new Error("Message template id is required");
  }

  const request = setHeaderOptions({ method: "GET" });

  return fetchData<MessageTemplateDetail>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    {
      ...request,
      signal: options?.signal,
    }
  );
}

export async function createMessageTemplate(
  payload: CreateMessageTemplateRequest
): Promise<MessageTemplateDetail> {
  if (!payload?.name) {
    throw new Error("Message template name is required");
  }

  if (!payload?.body?.trim()) {
    throw new Error("Message template content is required");
  }

  const request = setHeaderOptions({
    method: "POST",
    body: {
      key: toMessageTemplateKey(payload.name),
      content: payload.body,
      ...(payload.title ? { title: payload.title } : {}),
      ...(payload.description ? { description: payload.description } : {}),
      ...(typeof payload.is_active === "boolean"
        ? { is_active: payload.is_active }
        : {}),
      ...(payload.user_variable_keys?.length
        ? { user_variable_keys: payload.user_variable_keys }
        : {}),
    },
  });

  return fetchData<MessageTemplateDetail>(BASE_ROUTE, request);
}

export async function updateMessageTemplate(
  id: string,
  payload: UpdateMessageTemplateRequest
): Promise<MessageTemplateDetail> {
  if (!id) {
    throw new Error("Message template id is required");
  }

  const hasBody = typeof payload.body === "string";
  const hasName = typeof payload.name === "string";
  const hasTitle = typeof payload.title === "string";
  const hasDescription = typeof payload.description === "string";
  const hasIsActive = typeof payload.is_active === "boolean";
  const hasUserVariableKeys = Boolean(payload.user_variable_keys?.length);

  const request = setHeaderOptions({
    method: "PUT",
    body: {
      ...(hasName ? { key: toMessageTemplateKey(payload.name!) } : {}),
      ...(hasBody ? { content: payload.body } : {}),
      ...(hasTitle ? { title: payload.title } : {}),
      ...(hasDescription ? { description: payload.description } : {}),
      ...(hasIsActive ? { is_active: payload.is_active } : {}),
      ...(hasUserVariableKeys
        ? { user_variable_keys: payload.user_variable_keys }
        : {}),
    },
  });

  return fetchData<MessageTemplateDetail>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}
